/**
 * SearchableSelect - Composant Select avec recherche intégrée (type Select2)
 * Idéal pour les listes avec beaucoup d'options (articles, utilisateurs, etc.)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import './SearchableSelect.css';

export interface SelectOption {
  value: string;
  label: string;
  sublabel?: string;
  sublabelHighlight?: string; // Text to highlight in green/bold (price, stock, etc.)
}

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  noOptionsMessage?: string;
  className?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = '-- Sélectionner --',
  searchPlaceholder = 'Rechercher...',
  disabled = false,
  required = false,
  loading = false,
  noOptionsMessage = 'Aucune option trouvée',
  className = '',
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Trouver l'option sélectionnée
  const selectedOption = options.find((opt) => opt.value === value);

  // Filtrer les options par recherche
  const filteredOptions = options.filter((opt) => {
    const searchLower = search.toLowerCase();
    return (
      (opt.label && opt.label.toLowerCase().includes(searchLower)) ||
      (opt.sublabel && opt.sublabel.toLowerCase().includes(searchLower)) ||
      (opt.value && opt.value.toLowerCase().includes(searchLower))
    );
  });

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus sur la recherche quand on ouvre
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset highlight quand la recherche change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  // Scroll vers l'élément highlighté
  useEffect(() => {
    if (isOpen && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      setSearch('');
      setHighlightedIndex(0);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex].value);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSearch('');
          break;
        case 'Tab':
          setIsOpen(false);
          setSearch('');
          break;
      }
    },
    [isOpen, filteredOptions, highlightedIndex, handleSelect]
  );

  return (
    <div
      ref={containerRef}
      className={`searchable-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Bouton d'affichage */}
      <button
        type="button"
        className="searchable-select-trigger"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`searchable-select-value ${!selectedOption ? 'placeholder' : ''}`}>
          {loading ? (
            'Chargement...'
          ) : selectedOption ? (
            <>
              {selectedOption.label}
              {selectedOption.sublabel && (
                <span className="sublabel"> - {selectedOption.sublabel}</span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <span className="searchable-select-arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="searchable-select-dropdown">
          {/* Champ de recherche */}
          <div className="searchable-select-search">
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="searchable-select-search-input"
              onClick={(e) => e.stopPropagation()}
            />
            {search && (
              <button
                type="button"
                className="searchable-select-clear-search"
                onClick={() => setSearch('')}
              >
                ×
              </button>
            )}
          </div>

          {/* Liste des options */}
          <ul
            ref={listRef}
            className="searchable-select-options"
            role="listbox"
          >
            {!required && (
              <li
                className={`searchable-select-option ${value === '' ? 'selected' : ''} ${
                  highlightedIndex === -1 ? 'highlighted' : ''
                }`}
                onClick={() => handleSelect('')}
                onMouseEnter={() => setHighlightedIndex(-1)}
                role="option"
                aria-selected={value === ''}
              >
                <span className="option-placeholder">{placeholder}</span>
              </li>
            )}
            {filteredOptions.length === 0 ? (
              <li className="searchable-select-no-options">
                {noOptionsMessage}
              </li>
            ) : (
              filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  className={`searchable-select-option ${option.value === value ? 'selected' : ''} ${
                    highlightedIndex === index ? 'highlighted' : ''
                  }`}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={option.value === value}
                >
                  <span className="option-label">{option.label}</span>
                  {(option.sublabel || option.sublabelHighlight) && (
                    <span className="option-sublabel">
                      {option.sublabel}
                      {option.sublabelHighlight && (
                        <span className="option-highlight">{option.sublabelHighlight}</span>
                      )}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>

          {/* Footer avec compteur */}
          <div className="searchable-select-footer">
            {filteredOptions.length} / {options.length} option(s)
          </div>
        </div>
      )}
    </div>
  );
}
